import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Plus, X } from "lucide-react";
import { useUpdateCustomTool, useUpdateNativeTool, AgentTool } from "@/services/tool.query";
import { isNativeTool } from "@/services/tool.service";
import {
  sanitizeToolName,
  sanitizeParameterName,
  splitDynamicPrompt,
  stripProtocolFromDescription,
} from "@/utils/tool-name-utils";
import { SimplePhoneInput } from "@/components/ui/simple-phone-input";
import { ToolOptionsFields } from "./ToolOptionsFields";
import {
  customToolUpdates,
  editDialogInitialState,
  emptyHeaderRow,
  headerValues,
  toolSaveErrorMessage,
  type HeaderRow,
  type LiveRuntime,
  type ToolOptions,
} from "@/utils/tool-options";
import {
  customToolErrors,
  dialableNumber,
  savedHeaderErrors,
  smsToolErrors,
  transferToolErrors,
  type ToolFormErrors,
} from "./tool-form";
import { useFieldErrors } from "./use-field-errors";

interface EditToolDialogProps {
  tool: AgentTool;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The runtime of the editor's current (unsaved) model. */
  runtime: LiveRuntime;
}

export const EditToolDialog = ({
  tool,
  open,
  onOpenChange,
  runtime,
}: EditToolDialogProps) => {
  // Common fields, seeded from the row (the on-open effect below seeds them again)
  const [initial] = useState(() => editDialogInitialState(tool));
  const [toolName, setToolName] = useState(initial.toolName);
  const [description, setDescription] = useState(initial.description);
  const [endpointUrl, setEndpointUrl] = useState(initial.endpointUrl);
  const [httpMethod, setHttpMethod] = useState(initial.httpMethod);
  const [headers, setHeaders] = useState<HeaderRow[]>(initial.headers);
  const [parametersJson, setParametersJson] = useState(initial.parametersJson);
  const [timeoutSeconds, setTimeoutSeconds] = useState(initial.timeoutSeconds);
  const [toolOptions, setToolOptions] = useState<ToolOptions>(initial.toolOptions);
  const [saveError, setSaveError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Native tool specific fields. Same rule as the server: a row flagged native
  // under any other name is a custom tool.
  const isNative = isNativeTool(tool);
  const isTransferCall = isNative && tool.tool_name === "transfer_call";
  const isSendSms = isNative && tool.tool_name === "send_sms";
  const isEndCall = isNative && tool.tool_name === "end_call";
  
  // Transfer call fields
  const [transferMode, setTransferMode] = useState<"static" | "dynamic">(tool.transfer_call_mode || "static");
  const [transferDestination, setTransferDestination] = useState(tool.transfer_call_destination || "");
  const [transferType, setTransferType] = useState<"cold" | "warm">(tool.transfer_call_type || "cold");
  const [transferDescription, setTransferDescription] = useState(tool.description || "");
  const [transferDynamicPrompt, setTransferDynamicPrompt] = useState("");
  
  // Send SMS fields
  const [smsContentType, setSmsContentType] = useState<"prompt" | "static">(tool.sms_content_type || "prompt");
  const [smsStaticMessage, setSmsStaticMessage] = useState(tool.sms_static_message || "");
  // Saved credentials are never shown: the function answers the SID as a
  // placeholder and never the token. The fields start empty and, left so, keep them.
  const [smsCredentialsSaved, setSmsCredentialsSaved] = useState(Boolean(tool.twilio_account_sid));
  const [smsTwilioAccountSid, setSmsTwilioAccountSid] = useState("");
  const [smsTwilioAuthToken, setSmsTwilioAuthToken] = useState("");
  const [smsTwilioFromNumber, setSmsTwilioFromNumber] = useState(tool.twilio_from_number || "");
  
  const updateNativeTool = useUpdateNativeTool();
  const updateCustomTool = useUpdateCustomTool();

  const errors: ToolFormErrors = !isNative
    ? {
        ...customToolErrors({ toolName, storedToolName: tool.tool_name, endpointUrl, parametersJson, timeoutSeconds }),
        ...savedHeaderErrors(headers),
      }
    : isSendSms
      ? smsToolErrors({
          contentType: smsContentType,
          staticMessage: smsStaticMessage,
          accountSid: smsTwilioAccountSid,
          authToken: smsTwilioAuthToken,
          credentialsSaved: smsCredentialsSaved,
          fromNumber: smsTwilioFromNumber,
          timeoutSeconds,
        })
      : isTransferCall
        ? transferToolErrors({
            mode: transferMode,
            destination: transferDestination,
            dynamicPrompt: transferDynamicPrompt,
            description: transferDescription,
            descriptionRequired: true,
          })
        : {};
  const { field, error, check, reset: resetFieldErrors } = useFieldErrors(errors);

  const nativeInitialized = useRef(false);
  const customInitialized = useRef(false);

  // Reset initialized flags, and the errors shown, when dialog closes
  useEffect(() => {
    if (!open) {
      nativeInitialized.current = false;
      customInitialized.current = false;
      resetFieldErrors();
    }
  }, [open, resetFieldErrors]);

  // Native fields come from the row: the tool function mirrors it into the
  // agent's config, so the row is the one copy that is never stale.
  useEffect(() => {
    if (open && isNative && !nativeInitialized.current) {
      if (isTransferCall) {
        setTransferMode(tool.transfer_call_mode || "static");
        setTransferDestination(tool.transfer_call_destination || "");
        setTransferType(tool.transfer_call_type || "cold");
        const { description: cleanDesc, prompt } = splitDynamicPrompt(tool.description);
        setTransferDynamicPrompt(prompt);
        setTransferDescription(cleanDesc);
        setDescription(cleanDesc);
      } else if (isSendSms) {
        setDescription(stripProtocolFromDescription(tool.description || ""));
        setSmsContentType(tool.sms_content_type || "prompt");
        setSmsStaticMessage(tool.sms_static_message || "");
        setSmsCredentialsSaved(Boolean(tool.twilio_account_sid));
        setSmsTwilioAccountSid("");
        setSmsTwilioAuthToken("");
        setSmsTwilioFromNumber(tool.twilio_from_number || "");
      } else if (isEndCall) {
        setDescription(stripProtocolFromDescription(tool.description || ""));
      }
      nativeInitialized.current = true;
    }
  }, [open, tool, isNative, isTransferCall, isSendSms, isEndCall]);

  useEffect(() => {
    if (open && !customInitialized.current) {
      const fields = editDialogInitialState(tool);
      setToolName(fields.toolName);
      if (!isNative) {
        // Strip any legacy protocol block for UI display
        setDescription(stripProtocolFromDescription(fields.description));
        setEndpointUrl(fields.endpointUrl);
        setHttpMethod(fields.httpMethod);
        setHeaders(fields.headers);
        setParametersJson(fields.parametersJson);
        setTimeoutSeconds(fields.timeoutSeconds);
        setToolOptions(fields.toolOptions);
      }
      customInitialized.current = true;
    }
  }, [open, tool, isNative]);

  const handleAddHeader = () => {
    setHeaders([...headers, emptyHeaderRow()]);
  };

  const handleRemoveHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index: number, field: "key" | "value", value: string) => {
    setHeaders(headers.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const handleSubmit = async () => {
    // A refusal from an earlier try no longer applies to what is on screen now.
    setSaveError(undefined);
    // Nothing the server would refuse is sent: the reasons show under their fields.
    if (!check(contentRef.current)) return;
    setSaving(true);
    try {
      // One write each: the tool function mirrors a native row into the agent's
      // config. A native tool's endpoint, method, headers and parameters are never sent.
      if (isNative) {
        if (isTransferCall) {
          // Combine the cleaned base description with a dynamic transfer's prompt.
          // No execution-protocol block is appended.
          let finalDesc = stripProtocolFromDescription(transferDescription.trim());
          if (transferMode === "dynamic") {
            finalDesc = finalDesc
              ? `${finalDesc}\n\n[DYNAMIC PROMPT]\n${transferDynamicPrompt}`
              : `[DYNAMIC PROMPT]\n${transferDynamicPrompt}`;
          }

          await updateNativeTool.mutateAsync({
            tool,
            updates: {
              description: finalDesc,
              transfer_call_mode: transferMode,
              transfer_call_destination: transferMode === "static" ? dialableNumber(transferDestination) : null,
              transfer_call_type: transferType,
            },
          });
        } else if (isSendSms) {
          // The Twilio pair passed the check above.
          const twilioSid = smsTwilioAccountSid.trim();
          const twilioToken = smsTwilioAuthToken.trim();
          const twilioFrom = smsTwilioFromNumber.trim();

          await updateNativeTool.mutateAsync({
            tool,
            updates: {
              // The cleaned plain description (no execution-protocol block)
              description: stripProtocolFromDescription(description),
              sms_content_type: smsContentType,
              sms_static_message: smsContentType === "static" ? smsStaticMessage : null,
              timeout_seconds: timeoutSeconds,
              twilio_from_number: twilioFrom || null,
              // Saved credentials are kept by leaving them out: only what was
              // typed is sent. With none saved, or the saved ones removed, the
              // pair is sent as typed, and empty clears it.
              ...(smsCredentialsSaved
                ? {
                    ...(twilioSid ? { twilio_account_sid: twilioSid } : {}),
                    ...(twilioToken ? { twilio_auth_token: twilioToken } : {}),
                  }
                : { twilio_account_sid: twilioSid || null, twilio_auth_token: twilioToken || null }),
            },
          });
        } else if (isEndCall) {
          await updateNativeTool.mutateAsync({
            tool,
            updates: { description: stripProtocolFromDescription(description) },
          });
        }
      } else {
        let parameters: AgentTool["parameters"] = null;
        // The JSON and the parameter names passed the check above, so this cannot
        // fail and yields an object.
        if (parametersJson.trim()) {
          parameters = JSON.parse(parametersJson);


          // Sanitize parameter names
          if (parameters.properties) {
            const sanitizedProperties: Record<string, unknown> = {};
            for (const [key, value] of Object.entries(parameters.properties)) {
              const sanitizedKey = sanitizeParameterName(key);
              sanitizedProperties[sanitizedKey] = value;
            }
            parameters.properties = sanitizedProperties;

            // Also sanitize required array if it exists
            if (Array.isArray(parameters.required)) {
              parameters.required = parameters.required.map((req: string) => sanitizeParameterName(req));
            }
          }
        }

        // Save the cleaned plain description (no execution-protocol block)
        const finalDesc = stripProtocolFromDescription(description);

        await updateCustomTool.mutateAsync({
          tool,
          updates: customToolUpdates({
            toolName: sanitizeToolName(toolName),
            description: finalDesc,
            endpointUrl,
            httpMethod,
            headers: headerValues(headers),
            parameters,
            timeoutSeconds,
            toolOptions,
          }),
        });
      }

      onOpenChange(false);
    } catch (error) {
      // The hook toasts it too; the dialog stays open with the reason in it.
      setSaveError(toolSaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // A save in flight finishes in view: closing now would hide whether it worked.
  const handleOpenChange = (next: boolean) => {
    if (!next && saving) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent ref={contentRef} className="max-w-2xl max-h-[80vh] overflow-y-auto [&_[aria-invalid=true]]:border-destructive">
        <DialogHeader>
          <DialogTitle>
            {isTransferCall ? "Edit Transfer Call Tool" : 
             isSendSms ? "Edit Send SMS Tool" : 
             isEndCall ? "Edit End Call Tool" : 
             "Edit Tool"}
          </DialogTitle>
          <DialogDescription>
            {isNative 
              ? `Update your ${tool.tool_name.replace('_', ' ')} tool configuration.`
              : "Update your custom API endpoint configuration. Use {{userInput}} in the payload to insert dynamic values."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!isNative && (
            <div className="space-y-2">
              <Label htmlFor="edit-tool-name">Tool Name *</Label>
              <Input
                id="edit-tool-name"
                data-testid="tool-name"
                placeholder="e.g., weather_api, database_query"
                value={toolName}
                onChange={(e) => setToolName(e.target.value)}
                {...field("toolName", "edit-tool-name")}
              />
              {error("toolName", "edit-tool-name")}
              <p className="text-xs text-muted-foreground">
                Must start with a letter or underscore. Use underscores instead of spaces (e.g., weather_api).
              </p>
            </div>
          )}

          {/* Transfer Call Native Tool UI */}
          {isTransferCall && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="transfer-description">Transfer Description</Label>
                <Textarea
                  id="transfer-description"
                  placeholder="Describe when the agent should offer to transfer calls..."
                  value={transferDescription}
                  onChange={(e) => setTransferDescription(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                  {...field("description", "transfer-description")}
                />
                {error("description", "transfer-description")}
                <p className="text-xs text-muted-foreground">
                  Describe when and how the agent should transfer calls.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Transfer Mode</Label>
                <RadioGroup
                  value={transferMode}
                  onValueChange={(value: "static" | "dynamic") => setTransferMode(value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="static" id="edit-static" />
                    <Label htmlFor="edit-static" className="font-normal cursor-pointer">
                      Static
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="dynamic" id="edit-dynamic" />
                    <Label htmlFor="edit-dynamic" className="font-normal cursor-pointer">
                      Dynamic
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {transferMode === "static" && (
                <div className="space-y-2">
                  <Label htmlFor="transfer-destination">Phone Number *</Label>
                  <SimplePhoneInput
                    id="transfer-destination"
                    placeholder="1234567890"
                    value={transferDestination}
                    onChange={(value) => setTransferDestination(value)}
                    {...field("transferDestination", "transfer-destination")}
                  />
                  {error("transferDestination", "transfer-destination")}
                </div>
              )}

              {transferMode === "dynamic" && (
                <div className="space-y-2">
                  <Label htmlFor="transfer-dynamic-prompt">Dynamic Prompt *</Label>
                  <Textarea
                    id="transfer-dynamic-prompt"
                    placeholder="Describe when and how to determine the transfer destination..."
                    value={transferDynamicPrompt}
                    onChange={(e) => setTransferDynamicPrompt(e.target.value)}
                    rows={4}
                    {...field("transferDynamicPrompt", "transfer-dynamic-prompt")}
                  />
                  {error("transferDynamicPrompt", "transfer-dynamic-prompt")}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="transfer-type">Transfer Type</Label>
                <Select
                  value={transferType}
                  onValueChange={(value: "cold" | "warm") => setTransferType(value)}
                >
                  <SelectTrigger id="transfer-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cold">Cold Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Send SMS Native Tool UI */}
          {isSendSms && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sms-description">Send SMS Description</Label>
                <Textarea
                  id="sms-description"
                  placeholder="Describe when the agent should send SMS messages..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Describe when and how the agent should send SMS messages.
                </p>
              </div>

              <div className="space-y-2">
                <Label>SMS Content Type</Label>
                <RadioGroup
                  value={smsContentType}
                  onValueChange={(value: "prompt" | "static") => setSmsContentType(value)}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="prompt" id="sms-prompt" />
                    <Label htmlFor="sms-prompt" className="font-normal cursor-pointer">
                      Prompt (dynamic message from user)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="static" id="sms-static" />
                    <Label htmlFor="sms-static" className="font-normal cursor-pointer">
                      Static Sentence (predefined message)
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {smsContentType === "static" && (
                <div className="space-y-2">
                  <Label htmlFor="sms-static-message">Static Message</Label>
                  <Textarea
                    id="sms-static-message"
                    placeholder="Enter the predefined SMS message to send"
                    value={smsStaticMessage}
                    onChange={(e) => setSmsStaticMessage(e.target.value)}
                    rows={3}
                    {...field("smsStaticMessage", "sms-static-message")}
                  />
                  {error("smsStaticMessage", "sms-static-message")}
              </div>
              )}

              <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
                <div className="space-y-1">
                  <Label>Twilio Credentials</Label>
                  <p className="text-xs text-muted-foreground">
                    Required for numbers imported via SIP trunking (LiveKit), which carry no
                    Twilio API credentials.{" "}
                    {/* Saved credentials are kept by empty fields, so only removing them falls back. */}
                    {smsCredentialsSaved
                      ? "Remove the saved credentials to use the phone number's or workspace's Twilio credentials instead."
                      : "Leave empty to use the phone number's or workspace's Twilio credentials."}
                  </p>
                  {smsCredentialsSaved && (
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs text-muted-foreground">
                        Your saved Account SID and Auth Token are hidden. Leave both empty to keep them.
                      </p>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0"
                        onClick={() => setSmsCredentialsSaved(false)}
                      >
                        Remove saved credentials
                      </Button>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sms-twilio-sid">Account SID</Label>
                  <Input
                    id="edit-sms-twilio-sid"
                    placeholder={
                      smsCredentialsSaved ? "Saved — type a new Account SID to replace" : "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    }
                    value={smsTwilioAccountSid}
                    onChange={(e) => setSmsTwilioAccountSid(e.target.value)}
                    {...field("twilioAccountSid", "edit-sms-twilio-sid")}
                  />
                  {error("twilioAccountSid", "edit-sms-twilio-sid")}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sms-twilio-token">Auth Token</Label>
                  <Input
                    id="edit-sms-twilio-token"
                    type="password"
                    placeholder={smsCredentialsSaved ? "Saved — type a new Auth Token to replace" : "Your Twilio auth token"}
                    value={smsTwilioAuthToken}
                    onChange={(e) => setSmsTwilioAuthToken(e.target.value)}
                    {...field("twilioAuthToken", "edit-sms-twilio-token")}
                  />
                  {error("twilioAuthToken", "edit-sms-twilio-token")}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-sms-twilio-from">From Number (Optional)</Label>
                  <Input
                    id="edit-sms-twilio-from"
                    placeholder="+15551234567"
                    value={smsTwilioFromNumber}
                    onChange={(e) => setSmsTwilioFromNumber(e.target.value)}
                    {...field("twilioFromNumber", "edit-sms-twilio-from")}
                  />
                  {error("twilioFromNumber", "edit-sms-twilio-from")}
                  <p className="text-xs text-muted-foreground">
                    SMS is sent from this number. Leave empty to send from the call's own
                    number — it must be SMS-capable in the Twilio account above.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* End Call Native Tool UI */}
          {isEndCall && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="end-call-description">End Call Description</Label>
                <Textarea
                  id="end-call-description"
                  placeholder="Describe when to end calls..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Describe when and how the agent should end calls.
                </p>
              </div>
            </div>
          )}

          {/* Custom Tool Description */}
          {!isNative && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  data-testid="tool-description"
                  placeholder="Describe what this tool does..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={6}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Describe when and how the agent should use this tool.
                </p>
              </div>
            </div>
          )}

          {/* Standard Technical Fields only for Custom Tools (and not for native tools) */}
          {!isNative && (
            <>
              <div className="space-y-2">
                <Label htmlFor="edit-endpoint-url">Endpoint URL *</Label>
                <Input
                  id="edit-endpoint-url"
                  data-testid="tool-endpoint"
                  placeholder="https://api.example.com/endpoint"
                  value={endpointUrl}
                  onChange={(e) => setEndpointUrl(e.target.value)}
                  {...field("endpointUrl", "edit-endpoint-url")}
                />
                {error("endpointUrl", "edit-endpoint-url")}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-http-method">HTTP Method *</Label>
                <Select value={httpMethod} onValueChange={setHttpMethod}>
                  <SelectTrigger id="edit-http-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GET">GET</SelectItem>
                    <SelectItem value="POST">POST</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Headers</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddHeader}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Header
                  </Button>
                </div>
                <div className="space-y-2">
                  {headers.map((header, index) => (
                    <div key={index} className="space-y-1">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Key (e.g., Authorization)"
                          value={header.key}
                          onChange={(e) => handleHeaderChange(index, "key", e.target.value)}
                          className="flex-1"
                        />
                        <Input
                          // A saved value is never shown: left empty under its own name, it is kept.
                          placeholder={
                            header.saved && header.key === header.originalKey
                              ? "Saved value hidden — type to replace"
                              : "Value (e.g., Bearer token)"
                          }
                          value={header.value}
                          onChange={(e) => handleHeaderChange(index, "value", e.target.value)}
                          className="flex-1"
                          {...field(`headerValue${index}`, `edit-header-value-${index}`)}
                        />
                        {headers.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveHeader(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      {error(`headerValue${index}`, `edit-header-value-${index}`)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-parameters">Parameters Schema (JSON)</Label>
                <Textarea
                  id="edit-parameters"
                  placeholder={'{\n  "properties": {\n    "query": {\n      "type": "string",\n      "description": "Search query"\n    }\n  },\n  "required": ["query"]\n}'}
                  value={parametersJson}
                  onChange={(e) => setParametersJson(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                  {...field("parameters", "edit-parameters")}
                />
                {error("parameters", "edit-parameters")}
                <p className="text-xs text-muted-foreground">
                  Define the parameters this tool accepts (JSON Schema format). Must be valid JSON. Parameter names must start with a letter or underscore and only contain letters, numbers, and underscores.
                </p>
              </div>
            </>
          )}

          {!isNative && (
            <ToolOptionsFields idPrefix="edit" runtime={runtime} toolName={toolName} value={toolOptions} onChange={setToolOptions} />
          )}

          {/* Timeout and Speak options */}
          {!isEndCall && !isTransferCall && (
            <div className="space-y-2">
              <Label htmlFor="edit-timeout">Timeout (seconds)</Label>
              <Input
                id="edit-timeout"
                type="number"
                value={timeoutSeconds}
                onChange={(e) => setTimeoutSeconds(parseInt(e.target.value) || 30)}
                placeholder="30"
                min="5"
                max="300"
                {...field("timeout", "edit-timeout")}
              />
              {error("timeout", "edit-timeout")}
              <p className="text-xs text-muted-foreground">
                Maximum time to wait for tool execution (5-300 seconds)
              </p>
            </div>
          )}

        </div>

        {saveError && (
          <p role="alert" data-testid="tool-save-error" className="text-sm text-destructive">
            {saveError}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={saving} data-testid="tool-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} aria-busy={saving} data-testid="tool-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {saving ? "Saving…" : "Update Tool"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
